import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import {
  Conversation,
  Message,
  CreateConversationRequest,
  CreateConversationResponse,
  MessagesListParams,
  ChatUser,
  UserStatus,
  ConversationFile,
} from '../types/chat.types';
import { io, Socket } from 'socket.io-client';
import { BadgesState, ClientBadgesUpdatePayload } from '@/types/updates-hub';
import { initSocketLogger, logSocketEvent } from './socket-logger';
import { authService } from './auth.service';

const WEBSOCKET_URL = import.meta.env.VITE_SOCKET_SERVER_URL || 'http://localhost:3200';
const NAMESPACE_CHAT = import.meta.env.VITE_WEBSOCKET_NAMESPACE || '/chat';
const NAMESPACE_STATE = '/state';

class ChatService {
  private readonly BASE_URL = '/chat';
  
  socket: Socket | null = null;
  stateSocket: Socket | null = null;

  constructor() {
    initSocketLogger();
  }

  async getConversations(): Promise<Conversation[]> {
    const response = await api.get<ApiResponse<Conversation[]>>(`${this.BASE_URL}/conversations`);
    return unwrapApiResponse<Conversation[]>(response);
  }

  async getConversationById(conversationId: number): Promise<Conversation> {
    const response = await api.get<ApiResponse<Conversation>>(`${this.BASE_URL}/conversations/${conversationId}`);
    return unwrapApiResponse<Conversation>(response);
  }

  async createConversation(data: CreateConversationRequest): Promise<CreateConversationResponse> {
    const response = await api.post<ApiResponse<CreateConversationResponse>>(
      `${this.BASE_URL}/conversations`,
      data
    );
    return unwrapApiResponse<CreateConversationResponse>(response);
  }

  async getMessages(
    conversationId: number,
    params: MessagesListParams = {}
  ): Promise<Message[]> {
    const { page = 1, limit = 50 } = params;
    const response = await api.get<ApiResponse<Message[]>>(
      `${this.BASE_URL}/conversations/${conversationId}/messages`,
      {
        params: { page, limit },
      }
    );
    return unwrapApiResponse<Message[]>(response);
  }

  async getConversationFiles(conversationId: number): Promise<ConversationFile[]> {
    const response = await api.get<ApiResponse<ConversationFile[]>>(
      `${this.BASE_URL}/conversations/${conversationId}/files`,
    );
    return unwrapApiResponse<ConversationFile[]>(response);
  }

  async findChatUsers(query: string): Promise<ChatUser[]> {
    const response = await api.get<ApiResponse<ChatUser[]>>(`${this.BASE_URL}/find-users`, {
      params: { query },
    });
    return unwrapApiResponse<ChatUser[]>(response);
  }

  connect(token: string, onConnect?: () => void, onDisconnect?: () => void): void {
    if (this.socket?.connected && this.stateSocket?.connected) {
      return;
    }

    const chatUrl = `${WEBSOCKET_URL}${NAMESPACE_CHAT}`;
    const stateUrl = `${WEBSOCKET_URL}${NAMESPACE_STATE}`;

    // Функція, а не об'єкт (2026-09-25): socket.io викликає її на КОЖНЕ (пере)підключення - іде актуальний токен з
    // authService, а не той, що був при першому connect() (після оновлення токена перепідключення слали старий).
    const auth = (cb: (data: { token?: string }) => void) => cb({ token: authService.getAccessToken() ?? token });

    this.socket = io(chatUrl, {
      transports: ['websocket'],
      auth,
    });

    this.stateSocket = io(stateUrl, {
      transports: ['websocket'],
      auth,
    });

    this.socket.on('connect', () => {
      logSocketEvent('connect', 'chat_socket_connected', { id: this.socket?.id });
      if (onConnect && this.stateSocket?.connected) onConnect();
    });

    this.socket.on('disconnect', (reason) => {
      logSocketEvent('disconnect', 'chat_socket_disconnected', { reason });
      if (onDisconnect) onDisconnect();
    });

    this.socket.on('connect_error', (err) => {
      logSocketEvent('error', 'chat_socket_error', { error: err.message });
    });

    this.stateSocket.on('connect', () => {
      logSocketEvent('connect', 'state_socket_connected', { id: this.stateSocket?.id });
      if (onConnect && this.socket?.connected) onConnect();
    });

    this.stateSocket.on('disconnect', (reason) => {
      logSocketEvent('disconnect', 'state_socket_disconnected', { reason });
    });

    this.stateSocket.on('connect_error', (err) => {
      logSocketEvent('error', 'state_socket_error', { error: err.message });
    });

    this.socket.onAny((eventName, ...args) => {
      logSocketEvent('on', `chat:${eventName}`, args);
    });

    this.stateSocket.onAny((eventName, ...args) => {
      logSocketEvent('on', `state:${eventName}`, args);
    });
  }

  disconnect(): void {
    logSocketEvent('disconnect', 'manual_disconnect');
    this.socket?.disconnect();
    this.stateSocket?.disconnect();
    this.socket = null;
    this.stateSocket = null;
  }

  joinConversation(conversationId: number): void {
    if (!this.socket) {
      logSocketEvent('error', 'join_conversation_failed', { reason: 'socket_not_connected', conversationId });
      return;
    }
    logSocketEvent('emit', 'join_conversation', { conversationId });
    this.socket.emit('join_conversation', { conversationId });
  }

  leaveConversation(conversationId: number): void {
    if (!this.socket) {
      return;
    }
    logSocketEvent('emit', 'leave_conversation', { conversationId });
    this.socket.emit('leave_conversation', { conversationId });
  }

  sendRealtimeMessage(conversationId: number, message?: string, fileId?: number): void {
    if (!this.socket?.connected) {
      logSocketEvent('error', 'send_message_failed', { reason: 'socket_not_connected', conversationId });
      return;
    }
    const payload = { conversationId, message, fileId };
    logSocketEvent('emit', 'send_message', payload);
    this.socket.emit('send_message', payload);
  }

  sendUserWentAway(): void {
    if (!this.socket?.connected) return;
    logSocketEvent('emit', 'user_went_away');
    this.socket.emit('user_went_away');
  }

  sendUserCameBack(): void {
    if (!this.socket?.connected) return;
    logSocketEvent('emit', 'user_came_back');
    this.socket.emit('user_came_back');
  }

  sendTypingStart(conversationId: number): void {
    if (!this.socket?.connected) return;
    logSocketEvent('emit', 'typing_start', { conversationId });
    this.socket.emit('typing_start', { conversationId });
  }

  sendTypingStop(conversationId: number): void {
    if (!this.socket?.connected) return;
    logSocketEvent('emit', 'typing_stop', { conversationId });
    this.socket.emit('typing_stop', { conversationId });
  }

  onNewMessage(handler: (message: Message) => void): () => void {
    this.socket?.on('new_message', handler);
    return () => this.socket?.off('new_message', handler);
  }

  onMessageRead(handler: (data: { messageId: string; readBy: number; readAt: string }) => void): () => void {
    this.socket?.on('message_read', handler);
    return () => this.socket?.off('message_read', handler);
  }

  onUnreadCountUpdate(handler: (data: { total: number }) => void): () => void {
    this.stateSocket?.on('unread_count_update', handler);
    return () => this.stateSocket?.off('unread_count_update', handler);
  }

  onUserStatusChanged(handler: (data: { userId: number; status: UserStatus }) => void): () => void {
    const wrapper = (data: any) => {
        handler(data);
    };
    
    this.stateSocket?.on('user_status_changed', wrapper);
    this.socket?.on('user_status_changed', wrapper);

    return () => {
        this.stateSocket?.off('user_status_changed', wrapper);
        this.socket?.off('user_status_changed', wrapper);
    };
  }

  onUserTyping(handler: (data: { userName: string; conversationId: number }) => void): () => void {
    this.socket?.on('user_typing', handler);
    return () => this.socket?.off('user_typing', handler);
  }

  onUserStoppedTyping(handler: (data: { userName: string; conversationId: number }) => void): () => void {
    this.socket?.on('user_stopped_typing', handler);
    return () => this.socket?.off('user_stopped_typing', handler);
  }

  onPaymentStatus(handler: (data: { status: 'success' | 'error'; transactionId: number; planName?: string; errorMessage?: string; nextPaymentDate?: string }) => void): () => void {
    this.stateSocket?.on('payment:status', handler);
    return () => this.stateSocket?.off('payment:status', handler);
  }

  onBadgesUpdate(handler: (badges: BadgesState) => void): () => void {
    this.stateSocket?.on('updates-hub:badges', handler);
    return () => this.stateSocket?.off('updates-hub:badges', handler);
  }

  onClientBadgesUpdate(handler: (payload: ClientBadgesUpdatePayload) => void): () => void {
    this.stateSocket?.on('client-badges:update', handler);
    return () => this.stateSocket?.off('client-badges:update', handler);
  }

  async markAllAsRead(conversationId: number): Promise<void> {
    await api.put(`${this.BASE_URL}/conversations/${conversationId}/read-all`);
  }

  async markMessageAsRead(messageId: string): Promise<void> {
    await api.put(`${this.BASE_URL}/messages/${messageId}/read`);
  }

  getTotalUnreadCount(conversations: Conversation[]): number {
    return conversations.reduce((total, conv) => {
      const count = typeof conv.unreadCount === 'string' ? parseInt(conv.unreadCount) : conv.unreadCount;
      return total + (isNaN(count) ? 0 : count);
    }, 0);
  }

  sortConversationsByLastMessage(conversations: Conversation[]): Conversation[] {
    return [...conversations].sort((a, b) => {
      const timeA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const timeB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return timeB - timeA;
    });
  }

  formatMessageTime(timestamp: string): string {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMs = now.getTime() - messageTime.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInHours / 24;

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return `${diffInMinutes} хв`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} год`;
    } else if (diffInDays < 7) {
      const days = Math.floor(diffInDays);
      return `${days} дн`;
    } else {
      return messageTime.toLocaleDateString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
      });
    }
  }

  truncateMessage(message: string, maxLength: number = 100): string {
    if (message.length <= maxLength) {
      return message;
    }
    return message.substring(0, maxLength).trim() + '...';
  }
}

export const chatService = new ChatService();
